import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const EmailVerificationRequired = ({ user }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [resending, setResending] = useState(false);
  const [message, setMessage] = useState(null);

  const handleResendEmail = async () => {
    setResending(true);
    setMessage(null);
    try {
      await axios.post('/resend-verification-email');
      setMessage({
        type: 'success',
        text: t('emailVerification.success')
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: t('emailVerification.error')
      });
    } finally {
      setResending(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
        {/* Icône d'email */}
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 rounded-full flex items-center justify-center bg-yellow-100 text-yellow-600">
            <EnvelopeIcon className="w-8 h-8" />
          </div>
        </div>

        {/* Titre */}
        <h1 className="text-2xl font-bold text-gray-900 text-center mb-4">
          {t('emailVerification.title')}
        </h1>

        {/* Message */}
        <div className="text-center mb-6">
          <p className="text-gray-600 mb-3">
            {t('emailVerification.alertMessage')}
          </p>
          <p className="text-gray-700 mb-4">
            {t('emailVerification.description')}
          </p>
          
          {user?.email && (
            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <p className="text-sm text-gray-500 mb-1">{t('emailVerification.emailSentTo')}</p>
              <p className="font-medium text-gray-900">{user.email}</p>
            </div>
          )}
        </div>

        {/* Message de feedback */}
        {message && (
          <div className={`mb-4 p-3 rounded-lg ${
            message.type === 'success' 
              ? 'bg-green-50 text-green-800 border border-green-200' 
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {message.text}
          </div>
        )}

        {/* Bouton de renvoi */}
        <button
          onClick={handleResendEmail}
          disabled={resending}
          className="w-full bg-yellow-600 hover:bg-yellow-700 disabled:bg-yellow-400 text-white font-semibold py-3 px-4 rounded-lg transition-colors mb-3 flex items-center justify-center"
        >
          {resending ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {t('emailVerification.resending')}
            </>
          ) : (
            <>
              <EnvelopeIcon className="w-5 h-5 mr-2" />
              {t('emailVerification.resendEmail')}
            </>
          )}
        </button>

        {/* Bouton de déconnexion */}
        <button
          onClick={handleLogout}
          className="w-full border border-gray-300 text-gray-700 font-semibold py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors"
        >
          {t('logout.confirm')}
        </button>

        {/* Note */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            <span className="font-semibold">💡 {t('common.note')} :</span> {t('emailVerification.checkSpam')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationRequired;
